'use client';

/**
 * ComposerBriefCard — tender name + notes for the active draft.
 *
 * Reads `useCartStore.{tenderName, notes, projectName, activeIntent}`,
 * mutates via `setTenderName / setNotes`. Tender name only shown for
 * `rfq` and `po` (the auto-derived `{project} · {date}` placeholder
 * mirrors the legacy drawer); notes always present.
 *
 * @see klyde_edge/composition/cards/composer.py — composer_brief spec
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain procurement
 */

import { useMemo } from 'react';
import { useCardExport } from '@/lib/design/card-export';
import { useCartStore } from '@/lib/stores/cart-store';
import { useComposerLabel } from '@/lib/stores/total-matrix-store';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '@/components/copilot/rail-insights/rail-card';

export function ComposerBriefCard() {  const activeIntent = useCartStore((s) => s.activeIntent);
  const notesPlaceholderMcp = useComposerLabel('brief_notes_placeholder_mcp');
  const notesPlaceholderRfq = useComposerLabel('brief_notes_placeholder_rfq');
  const tenderName = useCartStore((s) => s.tenderName);
  const notes = useCartStore((s) => s.notes);
  const projectName = useCartStore((s) => s.projectName);
  const setTenderName = useCartStore((s) => s.setTenderName);

  // Scene-only export — the brief draft (tender name + notes).
  useCardExport(useMemo(() => ({
    label: 'Brief',
    content_key: 'composer_brief',
    surface: 'composer' as const,
    status: 'loaded' as const,
    query_key: null,
    text: [tenderName && `Tender: ${tenderName}`, notes && `Notes: ${notes}`]
      .filter(Boolean).join(' · ') || 'Brief (empty)',
    json: { tender_name: tenderName || null, notes: notes || null },
  }), [tenderName, notes]));
  const setNotes = useCartStore((s) => s.setNotes);

  const showTender = activeIntent === 'rfq' || activeIntent === 'po';
  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const tenderPlaceholder = projectName
    ? `${projectName} · ${today}`
    : `Tender · ${today}`;

  const inputClass = cn(
    text.body,
    'w-full h-7 px-2 rounded-md border border-border bg-background',
    'placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary',
  );

  return (
    <RailCard title="">
      <div className="space-y-2">
        {showTender && (
          <div>
            <label className={cn(text.muted, 'block mb-1')}>Tender name (optional)</label>
            <input
              type="text"
              value={tenderName}
              onChange={(e) => setTenderName(e.target.value)}
              placeholder={tenderPlaceholder}
              maxLength={100}
              className={inputClass}
            />
          </div>
        )}
        <div>
          <label className={cn(text.muted, 'block mb-1')}>Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              activeIntent === 'mcp' ? notesPlaceholderMcp : notesPlaceholderRfq
            }
            className={cn(
              text.body,
              'w-full h-16 px-2 py-1.5 rounded-md border border-border bg-background resize-none',
              'placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary',
            )}
          />
        </div>
      </div>
    </RailCard>
  );
}
